(() => {
  // Which field, if any, stops this gift from being complete: a valid email and a message are both required.
  const giftIssue = (email, message) => {
    if (!email.value.trim() || !email.checkValidity()) return 'email';
    if (!message.value.trim()) return 'message';
    return null;
  };

  const issueText = (issue) => (issue === 'email' ? window.giftStrings.emailError : window.giftStrings.messageError);

  const setGiftError = (root, field, text) => {
    const error = root.querySelector(`[data-gift-error="${field}"]`);
    error.hidden = !text;
    error.querySelector('.error-message').textContent = text || '';
    root.querySelector(`[data-gift-${field}]`).toggleAttribute('aria-invalid', !!text);
  };

  const bindCount = (root) => {
    const message = root.querySelector('[data-gift-message]');
    const count = root.querySelector('[data-gift-count]');
    const update = () => (count.textContent = `${message.value.length} / ${count.dataset.max}`);
    message.addEventListener('input', update);
    update();
    return update;
  };

  if (!customElements.get('gift-options')) {
    customElements.define(
      'gift-options',
      class GiftOptions extends HTMLElement {
        constructor() {
          super();
          this.toggle = this.querySelector('[data-gift-toggle]');
          this.body = this.querySelector('[data-gift-body]');
          this.email = this.querySelector('[data-gift-email]');
          this.from = this.querySelector('[data-gift-from]');
          this.message = this.querySelector('[data-gift-message]');
          this.onSubmit = this.onSubmit.bind(this);

          this.updateCount = bindCount(this);
          this.toggle.addEventListener('change', () => this.sync());
          this.email.addEventListener('input', () => setGiftError(this, 'email', null));
          this.message.addEventListener('input', () => setGiftError(this, 'message', null));
          this.email.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') event.preventDefault();
          });
          this.sync();
        }

        cartUpdateUnsubscriber = undefined;

        connectedCallback() {
          // Capture phase on the product-form element, so this runs before the form's own submit handler.
          this.productForm = this.closest('product-form');
          if (this.productForm) this.productForm.addEventListener('submit', this.onSubmit, true);

          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'product-form') this.reset();
          });
        }

        disconnectedCallback() {
          if (this.productForm) this.productForm.removeEventListener('submit', this.onSubmit, true);
          if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        }

        sync() {
          const enabled = this.toggle.checked;
          this.body.hidden = !enabled;
          // Disabled inputs are left out of the form data, so nothing is added to the cart unless it's a gift.
          this.email.disabled = !enabled;
          this.from.disabled = !enabled;
          this.message.disabled = !enabled;
          if (!enabled) {
            setGiftError(this, 'email', null);
            setGiftError(this, 'message', null);
          }
        }

        onSubmit(event) {
          if (!this.toggle.checked) return;
          const issue = giftIssue(this.email, this.message);
          if (!issue) return;

          event.preventDefault();
          event.stopPropagation();
          setGiftError(this, 'email', issue === 'email' ? issueText(issue) : null);
          setGiftError(this, 'message', issue === 'message' ? issueText(issue) : null);
          this[issue].focus();
        }

        reset() {
          this.toggle.checked = false;
          this.email.value = '';
          this.from.value = '';
          this.message.value = '';
          this.updateCount();
          this.sync();
        }
      }
    );
  }

  if (!customElements.get('cart-gift')) {
    customElements.define(
      'cart-gift',
      class CartGift extends HTMLElement {
        static pending = new Set();

        constructor() {
          super();
          this.toggle = this.querySelector('[data-gift-toggle]');
          this.body = this.querySelector('[data-gift-body]');
          this.email = this.querySelector('[data-gift-email]');
          this.from = this.querySelector('[data-gift-from]');
          this.message = this.querySelector('[data-gift-message]');
          this.copy = this.querySelector('[data-gift-copy]');
          this.copyWrapper = this.querySelector('[data-gift-copy-wrapper]');
          this.copyLabel = this.querySelector('[data-gift-copy-label]');
          this.status = this.querySelector('[data-gift-status]');

          this.updateCount = bindCount(this);
          this.toggle.addEventListener('change', () => this.onToggle());
          this.email.addEventListener('change', () => this.save());
          this.from.addEventListener('change', () => this.save());
          this.message.addEventListener('change', () => this.save());
          this.email.addEventListener('input', () => this.setError('email', null));
          this.message.addEventListener('input', () => this.setError('message', null));
          this.copy.addEventListener('change', () => this.onCopy());
          // Enter would otherwise submit the surrounding cart form.
          this.email.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            this.email.blur();
          });

          this.refreshCopy();
        }

        get container() {
          return this.closest('cart-items') || this.closest('cart-drawer-items');
        }

        get siblings() {
          return this.container ? [...this.container.querySelectorAll('cart-gift')] : [this];
        }

        // The first other line item that has a gift email; this line can copy its details.
        get copySource() {
          return this.siblings.find((gift) => gift !== this && gift.dataset.email) || null;
        }

        setError(field, text) {
          setGiftError(this, field, text);
        }

        refreshCopy() {
          const source = this.copySource;
          this.copyWrapper.hidden = !source;
          if (!source) return;

          this.copyLabel.textContent = window.giftStrings.copy.replace('[title]', source.dataset.title);
          this.copy.checked =
            !!this.dataset.email &&
            this.dataset.email === source.dataset.email &&
            this.dataset.from === source.dataset.from &&
            this.dataset.message === source.dataset.message;
        }

        clearFields() {
          this.email.value = '';
          this.from.value = '';
          this.message.value = '';
          this.updateCount();
          this.setError('email', null);
          this.setError('message', null);
        }

        onToggle() {
          if (this.toggle.checked) {
            this.body.hidden = false;
            this.refreshCopy();
            this.email.focus();
            return;
          }

          this.clearFields();
          this.body.hidden = true;
          this.save();
        }

        onCopy() {
          const source = this.copySource;
          if (!source) return;

          this.clearFields();
          if (this.copy.checked) {
            this.email.value = source.dataset.email;
            this.from.value = source.dataset.from || '';
            this.message.value = source.dataset.message || '';
            this.updateCount();
          } else {
            this.email.focus();
          }
          this.save();
        }

        isComplete() {
          return !this.toggle.checked || (!giftIssue(this.email, this.message) && !this.failed);
        }

        // Show what's stopping this gift from being complete, and take the customer to it.
        showIssue() {
          if (this.failed) {
            this.setError('email', window.giftStrings.saveError);
            this.email.focus();
            return;
          }
          const issue = giftIssue(this.email, this.message);
          this.setError(issue, issueText(issue));
          this[issue].focus();
        }

        save() {
          const email = this.email.value.trim();
          const from = email ? this.from.value.trim() : '';
          const message = email ? this.message.value.trim() : '';

          if (email && !this.email.checkValidity()) {
            this.setError('email', window.giftStrings.emailError);
            return;
          }
          if (
            email === (this.dataset.email || '') &&
            from === (this.dataset.from || '') &&
            message === (this.dataset.message || '')
          )
            return;

          this.failed = false;

          // /cart/change replaces a line's properties, so send the existing ones back too.
          // Empty values remove the property.
          // Liquid renders a line item's properties as a list of pairs; the cart API returns an object.
          const existing = JSON.parse(this.dataset.properties || '{}');
          const properties = Array.isArray(existing) ? Object.fromEntries(existing) : existing;
          properties['Gift email'] = email;
          properties['Gift from'] = from;
          properties['Gift message'] = message;

          const request = fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              line: Number(this.dataset.line),
              quantity: Number(this.dataset.quantity),
              properties,
            }),
            keepalive: true,
          })
            .then((response) => response.json())
            .then((cart) => {
              if (cart.status || cart.errors) throw new Error(cart.description || cart.errors);

              const item = cart.items[Number(this.dataset.line) - 1];
              this.dataset.email = email;
              this.dataset.from = from;
              this.dataset.message = message;
              this.dataset.properties = JSON.stringify((item && item.properties) || {});

              const rows = this.container ? this.container.querySelectorAll('.cart-item').length : cart.items.length;
              if (rows !== cart.items.length) {
                // Identical lines merged, so line numbers moved: re-render the cart.
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift', cartData: cart });
                return;
              }

              this.siblings.forEach((gift) => gift.refreshCopy && gift.refreshCopy());
              this.showStatus(window.giftStrings.saved);
            })
            .catch((error) => {
              console.error(error);
              this.failed = true;
              this.setError('email', window.giftStrings.saveError);
            });

          CartGift.pending.add(request);
          request.finally(() => CartGift.pending.delete(request));
        }

        showStatus(text) {
          this.status.textContent = text;
          clearTimeout(this.statusTimer);
          this.statusTimer = setTimeout(() => (this.status.textContent = ''), 2000);
        }
      }
    );

    // Let in-flight gift saves finish before checkout, and don't check out with an incomplete gift.
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('button[name="checkout"]');
        if (!button) return;

        const gifts = [...document.querySelectorAll('cart-gift')];

        // A save that failed earlier gets one more try before we give up on checking out.
        if (!button.dataset.giftRetried) gifts.filter((gift) => gift.failed).forEach((gift) => gift.save());

        const incomplete = gifts.find((gift) => !gift.isComplete());
        if (incomplete) {
          event.preventDefault();
          event.stopImmediatePropagation();
          delete button.dataset.giftRetried;
          incomplete.showIssue();
          return;
        }

        const CartGift = customElements.get('cart-gift');
        if (CartGift.pending.size) {
          event.preventDefault();
          event.stopImmediatePropagation();
          button.dataset.giftRetried = 'true';
          Promise.allSettled([...CartGift.pending]).then(() => button.click());
          return;
        }
        delete button.dataset.giftRetried;
      },
      true
    );
  }
})();
